/* eslint-disable no-restricted-syntax */
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import {
  getChartsShowCount,
  getTimeSpaceArray,
  supplementSingleData,
} from '@op/utils';
import React, { useMemo } from 'react';
import { channelColorMap, getChartPositionTop } from '../utils';
import { chartAllColors, dateTypeMap } from '@op/constants';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
  textShowElement,
} from '@op/components/ChartCommon/formatter';
import { reviewsFormat, sentimentFormat } from '@op/utils/value';
import { useIntl } from 'umi';
import { formatTimeXAxisText, getChartsGradientColor } from '@op/utils/chart';
import { channelInfo } from '@op/components/ChannelLabel';
// import { hexToRgba } from '@op/utils/systemUtil';


export default function EngagementCharts({
  data,
  filter,
  communityType,
  leftTitle,
  tooltipMaxTop,
  showSentiment,
  height= 205
}: any) {
  const { formatMessage } = useIntl();
  const { xAxis, seriesData } = useMemo(() => {
    const {
      timeline_of_start_time,
      timeline_of_end_time,
      timeline,
      sentimentTimeline,
    } = data;
    const seriesData: any = [];

    // 获取时间间隔，以时间去取数据
    const dateType = dateTypeMap[filter?.date_type];
    const format = dateType === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';

    const xAxis = getTimeSpaceArray(
      timeline_of_start_time,
      timeline_of_end_time,
      dateType,
      format
    );
    let index = 0;
    for (const key in timeline) {
      const element = timeline[key];
      const data = supplementSingleData(
        timeline_of_start_time,
        timeline_of_end_time,
        (element || []).map((item: { value: any; }) => ({
          ...item,
          count: item.value || 0,
        })),
        filter.date_type,
      );
      const color = channelColorMap[key] || chartAllColors[index];
      index += 1;
      seriesData.push({
        name: key,
        data: data.map((item) => item.count),
        yAxisIndex: 0,
        stack: 'total',
        type: 'line' as any,
        areaStyle: getChartsGradientColor(color, 0, 0.5),
        itemStyle: { color },
      });
    }
    seriesData.sort((a: any, b: any) => {
      const aValue = a.data.reduce((pre: number, cur: number) => pre + cur, 0);
      const bValue = b.data.reduce((pre: number, cur: number) => pre + cur, 0);
      return aValue - bValue;
    });

    if ((['all', 'posts'].includes(communityType)) && showSentiment){
      const data = supplementSingleData(
        timeline_of_start_time,
        timeline_of_end_time,
        (sentimentTimeline || []).map((item: { value: any; }) => ({
          ...item,
          count: item.value || 0,
        })),
        filter.date_type,
      );
      seriesData.push({
        name: 'Sentiment',
        data: data.map((item) => item.count),
        type: 'line' as any,
        itemStyle: { color: '#CFCC3A' },
        yAxisIndex: 1,
      });
    }

    return { xAxis, seriesData };
  }, [data]);

  const options = useMemo(() => ({
    tooltip: {
      // enterable: true,
      confine: false,
      position: (pos, params, dom, rect, size) => getChartPositionTop(pos, size, dom, tooltipMaxTop, 10),
      formatter: (params: any) => {
        const time = params?.[0]?.axisValue;
        const title = listTimeTitleFunc(time, filter.date_type, true);
        const sentimentItem = params.find(
          (item: { seriesName: string; }) => item.seriesName === 'Sentiment',
        );

        return formatChartParamsDate({
          params: params.filter(
            (item: { seriesName: string; }) => item.seriesName !== 'Sentiment',
          ),
          listTitleFunc: (params) => {
            const { name } = channelInfo(params.seriesName);
            return name;
          },
          isSort: true,
          valueFormat: (value) => reviewsFormat(value),
          dealwithRecords(records, total) {
            records.unshift({
              text: formatMessage({ id: 'Sum' }),
              value: reviewsFormat(total),
              originValue: total,
              originText: 'Sum',
            });
          },
          tablesConfigFunc: (records: Array<any>) => {
            const columns = [
              {
                showHeader: true,
                records,
                columns: [
                  {
                    key: 'text',
                    header: title,
                  },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: formatMessage({ id: leftTitle }),
                  },
                ],
              },
            ];

            if (sentimentItem) {
              const sentimentRecords = [
                {
                  text: textShowElement(
                    sentimentItem.marker,
                    formatMessage({ id: 'Sentiment' }),
                  ),
                  value: sentimentFormat(sentimentItem.value),
                  originValue: sentimentItem.value,
                  originText: 'Sentiment',
                },
              ];

              columns.unshift({
                showHeader: false,
                records: sentimentRecords,
                columns: [
                  {
                    key: 'text',
                    header: '',
                  },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: 'Sentiment',
                  },
                ],
              });
            }

            return columns;
          },
          // className: 'dbh-studio-community-tooltip'
        });
      },
    },
    axisPointer: getAxisPointer((data) => formatTimeXAxisText(data.value, filter?.date_type)),
    legend: {
      show: false,
    },
    xAxis: {
      data: xAxis,
      boundaryGap: false,
      axisLabel: {
        fontSize: 8,
        color: 'rgba(131, 134, 151, 0.6)',
        formatter: (value: string) =>
          formatTimeXAxisText(value, filter?.date_type),
      },
    },
    yAxis: [
      {
        type: 'value',
        splitNumber: 3,
        axisLabel: {
          fontSize: 8,
          color: 'rgba(131, 134, 151, 1)',
          formatter: (value: number) => {
            const data = getChartsShowCount(Math.abs(value), 1);
            return value >= 0 ? data : `-${data}`;
          },
        },
      },
      {
        type: 'value',
        splitNumber: 3,
        axisLabel: {
          fontSize: 8,
          color: 'rgba(131, 134, 151, 1)',
        },
      },
    ],
    grid: {
      top: 10,
      bottom: 0,
      containLabel: true,
    },
    series: seriesData,
  }), [seriesData, xAxis, formatMessage, tooltipMaxTop]);

  const empty = useMemo(() => {
    const { timeline } = data || {};
    if (!timeline) return true;
    if (xAxis.length === 0) return true;
    let flags = false;
    for (const key in timeline) {
      const element = timeline[key];
      flags = flags || element.length === 0;
    }
    return flags;
  }, [data]);

  return (
    <div className="community-chart-box community-chart-box-legend">
      <EChartCommon
        height={height}
        emptyHeight={240}
        hasEmptyDesc={false}
        optionsProps={{ type: 'line', excludeOptions: ['axisPointer'] }}
        empty={empty}
        leftTitle={
          <div className="community-chart-box-legend-left">
            {formatMessage({ id: leftTitle })}
          </div>
        }
        rightTitle={
          ['all', 'posts'].includes(communityType) && showSentiment ? (
            <div className="community-chart-box-legend-right">
              {formatMessage({ id: 'Sentiment' })}
            </div>
          ) : null
        }
        options={options}
      ></EChartCommon>
    </div>
  );
}
